import type { AmplienceTopLevelNavigationCategory } from './types';

import { LOCALE_REGEX } from '~/utilities/constants/regex';

type AmplienceColumn = NonNullable<AmplienceTopLevelNavigationCategory['columns']>[number];
type AmplienceLink = NonNullable<AmplienceColumn['links']>[number];

/** A content link the navigation can render: it has both a label and a URL. */
export type ValidNavigationLink = AmplienceLink & { label: string; href: string };

/**
 * Returns an id factory for one list: ids come from the content (`base`), and a repeated
 * base gets a numeric suffix so sibling keys stay unique (`men`, `men-2`, …).
 */
export const createUniqueIdFactory = (): ((base: string) => string) => {
  const counts = new Map<string, number>();

  return base => {
    const count = (counts.get(base) ?? 0) + 1;

    counts.set(base, count);

    return count === 1 ? base : `${base}-${count}`;
  };
};

/**
 * Id of a category's visible name. The toggle and the panel are labelled by it
 * (`aria-labelledby`), so screen readers announce the localized category name and their own
 * localized expanded/collapsed state instead of hardcoded English text.
 */
export const getCategoryLabelId = (index: number): string =>
  `top-level-navigation-category-${index}`;

export const isExternalLink = (
  url?: string | null,
  isExternal?: boolean,
  target?: string | null
): boolean =>
  isExternal === true ||
  target === '_blank' ||
  target === 'NEWWINDOW' ||
  (!!url && (url.startsWith('http://') || url.startsWith('https://')));

export const isValidNavigationLink = (link?: AmplienceLink | null): link is ValidNavigationLink =>
  !!link?.label && !!link.href;

/**
 * Id-safe slug of a content label, for ids derived from content when no id is set. Ids end up in
 * `id` and `aria-labelledby` (a space-separated id list), so they must not contain spaces.
 */
export const toIdSlug = (label: string | null | undefined, fallback: string): string =>
  label
    ?.toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || fallback;

/**
 * Path part of an internal URL for comparisons: no query / hash, no locale segment, no trailing
 * slash, lowercase. Content links may carry a locale (`FesLink` normalizes it), while
 * `router.asPath` never does, so the locale is dropped on both sides.
 */
export const toComparablePath = (url: string): string => {
  const segments = url.split(/[?#]/)[0].split('/');

  if (LOCALE_REGEX.test(segments[1] ?? '')) {
    segments.splice(1, 1);
  }

  return segments.join('/').replace(/\/+$/, '').toLowerCase();
};

/**
 * Whether `path` is the internal `url` itself or a page below it (`/shop/men` → `/shop/men/jeans`).
 * Query, hash, trailing slash and case are ignored on both sides. The site root (`/`) never
 * matches: it would claim every page.
 */
export const matchesPath = (url: string | null | undefined, path: string): boolean => {
  if (!url || isExternalLink(url)) {
    return false;
  }

  const base = toComparablePath(url);

  if (!base) {
    return false;
  }

  const current = toComparablePath(path);

  return current === base || current.startsWith(`${base}/`);
};

/** Fallback for the localized `opensInNewTabLabel` of the Top Level Navigation Links content. */
export const DEFAULT_OPENS_IN_NEW_TAB_LABEL = '(opens in a new tab)';

/** Visually hidden text announced after links that open in a new tab (desktop and mobile TLN). */
export const getOpensInNewTabLabel = (opensInNewTabLabel?: string | null): string =>
  opensInNewTabLabel || DEFAULT_OPENS_IN_NEW_TAB_LABEL;
